import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  TipoProductoEnumChoice,
  a11yProps,
  gridSize,
  gridSizeMdLg10,
  gridSizeMdLg12,
  gridSizeMdLg3,
  gridSizeMdLg4,
  newActivoSchema,
} from "@/shared";
import {
  useCreateActivos,
  useUpdateActivos,
  usefetchGrupoActivos,
  useFetchMantenimiento,
  useFetchAsignaciones,
  usefetchDeprecaciones,
} from "@/shared/actions/erp";
import {
  CustomAutocomplete,
  SampleDatePicker,
  SingleTableBoxScene,
  NestedTabsScene,
  FormTabsOnly,
  CustomTabPanel,
  TabTextLabel,
  SampleTable,
  SampleCheckbox,
  CustomTextField,
  CustomNumberTextField,
} from "@/shared/components/ui";
import {
  Activos,
  GrupoActivo,
  Mantenimiento,
  FechasRango,
  Asignaciones,
  Deprecacion,
} from "@/shared/interfaces";
import {
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tab,
  TextField,
} from "@mui/material";
import { useUiStore } from "@/store/ui";
import { MRT_ColumnDef } from "material-react-table";
import DateRangeIcon from "@mui/icons-material/DateRange";

const returnUrlActivos = "/activos/activos";
export interface SaveActivosProps {
  titlePage: string;
  editActivos?: Activos;
}

type SaveFormData = {
  codigo: string;
  codigo_producto?: string;
  numero_referencia?: string;
  tipo_producto?: string;
  nombre: string;
  descripcion: string;
  vida_util: number;
  valor_residual: string;
  anios_deprecacion?: number;
  meses_deprecacion: number;
  fecha_adquisicion: string;
  costo_adquisicion: string;
  porcentaje_depreciacion: string;
  valor_deprecacion_anual?: string;
  valor_deprecacion_mensual?: string;
  valor_deprecacion_diaria: string;
  observaciones: string;
  estado: boolean;
  grupo_activo: string;
  fecha_before?: string;
  fecha_after?: string;
  estado_fisico?: string;
};



const SaveActivos: React.FC<SaveActivosProps> = ({
  titlePage,
  editActivos,
}) => {
  const navigate = useNavigate();
  ///* local state
  const [shrink, setShrink] = useState(false);
  const [activoChange, setActivoChange] = useState(1);

  const TipoProductoOptions = Object.entries(TipoProductoEnumChoice).map(
    ([key, value]) => ({ label: key, value })
  );

  const [dateRange, setDateRange] = useState<FechasRango | "">("");

  const handleDateRangeChange = (event: SelectChangeEvent<FechasRango>) => {
    setDateRange(event.target.value as FechasRango);
  };

  const handleActivoChange = useCallback((_event: any, value: any) => {
    setActivoChange(value);
  }, []);

  ///* fetch data
  const { data: GrupoActivoPaging, isLoading: isLoadingGrupoActivo } =
    usefetchGrupoActivos({
      page_size: 600,
    });

  const { data: DeprecacionesPaging } = usefetchDeprecaciones({
    page_size: 600,
    activo: editActivos?.codigo ?? "",
  });
  ///* form
  const form = useForm<SaveFormData>({
    resolver: yupResolver(newActivoSchema),
    defaultValues: { estado: false },
  });

  const {
    handleSubmit,
    control,
    register,
    reset,
    formState: { errors, isValid },
  } = form;

  ///* mutations
  const createActivos = useCreateActivos({
    navigate,
    returnUrl: returnUrlActivos,
  });
  const updateActivos = useUpdateActivos({
    navigate,
    returnUrl: returnUrlActivos,
  });
  ///* handlers

  const onSave = () => {
    if (!isValid) return;

    ///* upd
    if (editActivos?.id_activo) {
      return updateActivos.mutate({
        id: editActivos.id_activo,
        data: form.getValues() as any, // percent
      });
    }

    ///* create
    return createActivos.mutate(form.getValues() as any);
  };
  ///*fetch
  const onChangeGrupoActivo = (value: string) => {
    form.setValue("grupo_activo", value);
    const selectedOption = GrupoActivoPaging?.data.data.find(
      (option) => option.codigo === value
    );
    if (selectedOption) {
      form.setValue("anios_deprecacion", selectedOption.anios_deprecacion);
      form.setValue("porcentaje_depreciacion", selectedOption.porcentaje_depreciacion);
      form.setValue("meses_deprecacion", selectedOption.meses_deprecacion);
      form.setValue("valor_deprecacion_mensual", selectedOption.valor_deprecacion_mensual);
      form.setValue("valor_deprecacion_anual", selectedOption.valor_deprecacion_anual);
      form.setValue("valor_deprecacion_diaria", selectedOption.valor_deprecacion_diaria);
      form.setValue("valor_residual", selectedOption.valor_residual);
      form.setValue("vida_util", selectedOption.vida_util);
    }
  };



  useEffect(() => {
    if (editActivos) {
      setShrink(true);
      reset({
        ...editActivos,
      } as any);
    }
  }, [editActivos]);
  const setAlert = useUiStore((s) => s.setAlert);

  ///* columns
  const columns = useMemo<MRT_ColumnDef<Mantenimiento>[]>(
    () => [
      {
        accessorKey: "activo",
        header: "activo",
        size: 300,
      },
      {
        accessorKey: "codigo",
        header: "codigo",
        size: 300,
      },
      {
        accessorKey: "descripcion",
        header: "descripcion",
        size: 300,
      },
      {
        accessorKey: "encargado",
        header: "encargado",
        size: 300,
      },
      {
        accessorKey: "fecha_inicio",
        header: "fecha inicio",
        size: 300,
      },
      {
        accessorKey: "fecha_fin",
        header: "fecha fin",
        size: 300,
      },
    ],
    []
  );

  const columnsAsignaciones = useMemo<MRT_ColumnDef<Asignaciones>[]>(
    () => [
      {
        accessorKey: "activo",
        header: "activo",
        size: 300,
      },
      {
        accessorKey: "codigo",
        header: "codigo",
        size: 300,
      },
      {
        accessorKey: "descripcion",
        header: "descripcion",
        size: 300,
      },
      {
        accessorKey: "encargado",
        header: "encargado",
        size: 300,
      },
    ],
    []
  );
  const columnsDepreciaciones = useMemo<MRT_ColumnDef<Deprecacion>[]>(
    () => [
      {
        accessorKey: "fecha",
        header: "fecha",
        size: 300,
      },
      {
        accessorKey: "mes",
        header: "mes",
        size: 300,
      },
      {
        accessorKey: "dias",
        header: "dias",
        size: 300,
      },
      {
        accessorKey: "depreciacion_mensual",
        header: "depreciacion mensual",
        size: 300,
      },
      {
        accessorKey: "depreciacion_acumalado",
        header: "depreciacion acumalado",
        size: 300,
      },
      {
        accessorKey: "saldo_depreciacion",
        header: "saldo depreciacion",
        size: 300,
      },

    ],
    []
  );
  useEffect(() => {
    let newFechaAfter = null;
    let newFechaBefore = null;
    const now = new Date();
    switch (dateRange) {
      case FechasRango.HOY:
        newFechaAfter = newFechaBefore = now.toISOString().slice(0, 10);
        break;
      case FechasRango.MES_ACTUAL:
        newFechaAfter = new Date(now.getFullYear(), now.getMonth(), 1)
          .toISOString()
          .slice(0, 10);
        newFechaBefore = now.toISOString().slice(0, 10);
        break;
      case FechasRango.MESA_NTERIOR:
        newFechaAfter = new Date(now.getFullYear(), now.getMonth() - 1, 1)
          .toISOString()
          .slice(0, 10);
        newFechaBefore = new Date(now.getFullYear(), now.getMonth(), 0)
          .toISOString()
          .slice(0, 10);
        break;
      case FechasRango.ANIO_ACTUAL:
        newFechaAfter = new Date(now.getFullYear(), 0, 1)
          .toISOString()
          .slice(0, 10);
        newFechaBefore = now.toISOString().slice(0, 10);
        break;
      case FechasRango.ANIO_ANTERIOR:
        newFechaAfter = new Date(now.getFullYear() - 1, 0, 1)
          .toISOString()
          .slice(0, 10);
        newFechaBefore = new Date(now.getFullYear() - 1, 11, 31)
          .toISOString()
          .slice(0, 10);
        break;
    }
    setFechaAfter(newFechaAfter);
    setFechaBefore(newFechaBefore);
  }, [dateRange]);

  const [fechaAfter, setFechaAfter] = useState<string | null>(null);
  const [fechaBefore, setFechaBefore] = useState<string | null>(null);

  const { data: MantenimientoPagingRes } = useFetchMantenimiento({
    activo: editActivos?.codigo ?? "",
    fecha_inicio_after: fechaAfter ?? undefined,
    fecha_fin_before: fechaBefore ?? undefined,
  });

  const { data: AsignacionesPagingRes } = useFetchAsignaciones({
    activo: editActivos?.codigo ?? "",
    fecha_asignacion_before: fechaAfter ?? undefined,
    fecha_devolucion_after: fechaBefore ?? undefined,
  });
  const onChangeGrupoCatologo = (value: string) => {
    form.setValue("tipo_producto", value);
  };


  useEffect(() => {
    form.setValue("fecha_after", fechaAfter ?? undefined);
    form.setValue("fecha_before", fechaBefore ?? undefined);
  }, [fechaAfter, fechaBefore]);
  return (
    <SingleTableBoxScene
      title={titlePage}
      //title={'Activo'}
      enableBackBtn={true}
      backPageUrl="/activos/activos"
      visibleReport={false}
      enableCreateOrEditBtn={true}
      textCreateOrEditBtn="Guardar"
      fnCreateOrEdit={handleSubmit(onSave, () => {
        setAlert(true, "Faltan campos por llenar", "error");
      })}
    >
      <NestedTabsScene
        tabs={
          <FormTabsOnly
            onChange={handleActivoChange}
            value={activoChange}
          // justifyContent="flex-end"
          >
            <Tab label="Datos" value={1} {...a11yProps(1)} />
            <Tab
              label="Historial mantenimientos"
              value={2}
              {...a11yProps(2)}
            />
            <Tab label="Historial asignaciones" value={3} {...a11yProps(3)} />
            <Tab label="Depreciaciones" value={4} {...a11yProps(4)} />
          </FormTabsOnly>
        }
      >
        <Grid container spacing={3}>
          <CustomTabPanel index={activoChange} value={1}>
            <SampleDatePicker
              label="Fecha adquición"
              control={control}
              error={errors.fecha_adquisicion}
              helperText={errors.fecha_adquisicion?.message}
              name="fecha_adquisicion"
              textFieldKey="fecha_adquisicion"
              defaultValue={new Date().toISOString().slice(0, 10)}
              size={gridSizeMdLg4}
            />
            <CustomTextField
              name="codigo"
              label="Codigo"
              control={form.control}
              defaultValue={form.getValues().codigo || ""}
              error={errors.codigo}
              helperText={errors.codigo?.message}
              shrink={shrink}
              size={gridSizeMdLg4}
            />
            <CustomTextField
              name="nombre"
              label="Nombre"
              control={form.control}
              defaultValue={form.getValues().nombre || ""}
              error={errors.nombre}
              helperText={errors.nombre?.message}
              shrink={shrink}
              size={gridSizeMdLg4}
            />
            <CustomTextField
              name="descripcion"
              label="Descripcion"
              control={form.control}
              defaultValue={form.getValues().descripcion || ""}
              error={errors.descripcion}
              helperText={errors.descripcion?.message}
              shrink={shrink}
              size={gridSizeMdLg4}
            />

            <CustomAutocomplete
              label="Tipo de producto"
              name="tipo_producto"
              control={form.control}
              defaultValue={form.getValues().tipo_producto}
              error={errors.tipo_producto}
              helperText={errors.tipo_producto?.message}
              options={TipoProductoOptions}
              isLoadingData={false}
              valueKey="value"
              optionLabelForEdit={form.getValues().tipo_producto}
              onChangeValue={onChangeGrupoCatologo}
              size={gridSizeMdLg4}
            />


            <CustomAutocomplete<GrupoActivo>
              label="Grupo activo"
              name="grupo_activo"
              //// options
              options={GrupoActivoPaging?.data?.data || []}
              // label & search by name
              valueKey="codigo"
              //
              defaultValue={form.getValues().grupo_activo || ""}
              optionLabelForEdit={form.getValues().grupo_activo || ""}
              isLoadingData={isLoadingGrupoActivo}
              //// validation
              control={form.control}
              error={errors.grupo_activo}
              helperText={errors.grupo_activo?.message}
              onChangeValue={onChangeGrupoActivo}
              size={gridSizeMdLg4}
            />
            <CustomNumberTextField
              label="Vida util"
              name="vida_util"
              error={errors.vida_util}
              helperText={errors.vida_util?.message}
              size={gridSizeMdLg4}
              //readonly={true}
              control={form.control}
              defaultValue={form.getValues().vida_util || ""}
            />
            <CustomNumberTextField
              label="Meses depreciacion"
              name="meses_deprecacion"
              error={errors.meses_deprecacion}
              helperText={errors.meses_deprecacion?.message}
              shrink={shrink}
              size={gridSizeMdLg4}
              //readonly={true}
              control={form.control}
              defaultValue={form.getValues().meses_deprecacion || ""}
            />

            <CustomTextField
              label="costo adquisicion"
              error={errors.costo_adquisicion}
              helperText={errors.costo_adquisicion?.message}
              shrink={shrink}
              size={gridSizeMdLg4}
              control={form.control}
              name="costo_adquisicion"
              defaultValue={form.getValues().costo_adquisicion || "  "}
            />
            <CustomTextField
              label="Valor deprecacion anual"
              error={errors.valor_deprecacion_anual}
              helperText={errors.valor_deprecacion_anual?.message}
              shrink={shrink}
              size={gridSizeMdLg4}
              readonly={true}
              control={form.control}
              name="valor_deprecacion_anual"
              defaultValue={form.getValues().valor_deprecacion_anual}
            />
            <CustomTextField
              label="Valor residual"
              error={errors.valor_residual}
              helperText={errors.valor_residual?.message}
              shrink={shrink}
              size={gridSizeMdLg4}
              readonly={true}
              control={form.control}
              name="valor_residual"
              defaultValue={form.getValues().valor_residual || ""}
            />
            <CustomNumberTextField
              label="años depreciacion"
              name="anios_deprecacion"
              error={errors.anios_deprecacion}
              helperText={errors.anios_deprecacion?.message}
              shrink={shrink}
              size={gridSizeMdLg4}
              control={form.control}
              defaultValue={form.getValues().anios_deprecacion || ""}
            />
            <CustomTextField
              label="Porcentaje depreciacion"
              error={errors.porcentaje_depreciacion}
              helperText={errors.porcentaje_depreciacion?.message}
              shrink={shrink}
              readonly={true}
              size={gridSizeMdLg4}
              control={form.control}
              name="porcentaje_depreciacion"
              defaultValue={form.getValues().porcentaje_depreciacion || ""}
            />
            <Grid item {...gridSizeMdLg4}>
              <SampleCheckbox
                label="Estado"
                name="estado"
                control={form.control}
                defaultValue={!!form.getValues().estado || false}
                isState
              />
            </Grid>
            <Grid item {...gridSizeMdLg4}>
              <TextField
                {...register("observaciones", {
                  required: "La observacion es obligatoria",
                })}
                id="observacion"
                label="Observación"
                fullWidth
                rows={4}
                multiline
                error={Boolean(errors.observaciones)}
                helperText={errors.observaciones?.message}
              />
            </Grid>

          </CustomTabPanel>
        </Grid>
      </NestedTabsScene>
      <CustomTabPanel index={activoChange} value={2}>
        <Grid
          container
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
        >
          <Grid item>
            <SampleDatePicker
              label="Desde"
              control={control}
              error={errors.fecha_after}
              helperText={errors.fecha_after?.message}
              name="fecha_after"
              textFieldKey="fecha_after"
              defaultValue={fechaAfter}
              size={gridSizeMdLg10}
              onChangeValue={(date) => setFechaAfter(date)}
              key={fechaAfter}
            />
          </Grid>
          <Grid item>
            <SampleDatePicker
              label="Hasta"
              control={control}
              error={errors.fecha_before}
              helperText={errors.fecha_before?.message}
              name="fecha_before"
              textFieldKey="fecha_before"
              defaultValue={fechaBefore}
              size={gridSizeMdLg10}
              onChangeValue={(date) => setFechaBefore(date)}
              key={fechaBefore}
            />
          </Grid>
          <Grid item>
            <Select
              value={dateRange}
              onChange={handleDateRangeChange}
              startAdornment={
                <InputAdornment position="start">
                  <DateRangeIcon />
                </InputAdornment>
              }
            >
              {Object.values(FechasRango).map((value) => (
                <MenuItem key={value.replace(' ', '-')} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
        <Grid item {...gridSize}>
          <Paper className="paper-producto">
            <Grid
              container
              spacing={1}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Grid item>
                <TabTextLabel
                  text={"Historial de mantenimientos"}
                  mt={0}
                  pb={0}
                  pl={0}
                  pt={0}
                />
              </Grid>
              <div className="linea"></div>
              <Grid item xs={12}>
                <SampleTable<Mantenimiento>
                  columns={columns}
                  data={MantenimientoPagingRes?.data?.data || []}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </CustomTabPanel>
      <CustomTabPanel index={activoChange} value={3}>
        <Grid
          container
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
        >
          <Grid item>
            <SampleDatePicker
              label="Desde"
              control={control}
              error={errors.fecha_after}
              helperText={errors.fecha_after?.message}
              name="fecha_after"
              textFieldKey="fecha_after"
              defaultValue={fechaAfter}
              size={gridSizeMdLg10}
              onChangeValue={(date) => setFechaAfter(date)}
              key={fechaAfter}
            />
          </Grid>
          <Grid item>
            <SampleDatePicker
              label="Hasta"
              control={control}
              error={errors.fecha_before}
              helperText={errors.fecha_before?.message}
              name="fecha_before"
              textFieldKey="fecha_before"
              defaultValue={fechaBefore}
              size={gridSizeMdLg10}
              onChangeValue={(date) => setFechaBefore(date)}
              key={fechaBefore}
            />
          </Grid>
          <Grid item>
            <Select
              value={dateRange}
              onChange={handleDateRangeChange}
              startAdornment={
                <InputAdornment position="start">
                  <DateRangeIcon />
                </InputAdornment>
              }
            >
              {Object.values(FechasRango).map((value) => (
                <MenuItem key={value.replace(' ', '-')} value={value}>{value}</MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
        <Grid item {...gridSize}>
          <Paper className="paper-producto">
            <Grid
              container
              spacing={1}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Grid item>
                <TabTextLabel
                  text={"Asignaciones"}
                  mt={0}
                  pb={0}
                  pl={0}
                  pt={0}
                />
              </Grid>
              <div className="linea"></div>
              <Grid item xs={12}>
                <SampleTable<Asignaciones>
                  columns={columnsAsignaciones}
                  data={AsignacionesPagingRes?.data?.data || []}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </CustomTabPanel>
      <Grid container spacing={3}>
        <CustomTabPanel index={activoChange} value={4}>
          <SampleDatePicker
            label="Fecha adquisicion"
            control={control}
            error={errors.fecha_adquisicion}
            helperText={errors.fecha_adquisicion?.message}
            name="fecha_adquisicion"
            textFieldKey="fecha_adquisicion"
            defaultValue={new Date().toISOString().slice(0, 10)}
            size={gridSizeMdLg3}
            readonly={true}

          />
          <CustomNumberTextField
            label="Vida util"
            name="vida_util"
            control={form.control}
            //register={register("vida_util")}
            error={errors.vida_util}
            helperText={errors.vida_util?.message}
            shrink={!!form.getValues("vida_util")}
            //readOnly={true}
            defaultValue={form.getValues().vida_util || ''}
            size={gridSizeMdLg3}
          />
          <CustomNumberTextField
            label="Meses depreciacion"
            name="meses_deprecacion"
            //register={register("meses_deprecacion")}
            control={form.control}
            error={errors.meses_deprecacion}
            helperText={errors.meses_deprecacion?.message}
            shrink={shrink}
            defaultValue={form.getValues().meses_deprecacion || ''}
            ///readOnly={true}
            size={gridSizeMdLg3}
          />

          <CustomNumberTextField
            label="Costo_adquisicion"
            name="costo_adquisicion"
            error={errors.costo_adquisicion}
            helperText={errors.costo_adquisicion?.message}
            shrink={shrink}
            min={0}
            max={10000}
            control={form.control}
            defaultValue={form.getValues().costo_adquisicion || ''}
            size={gridSizeMdLg3}
          />

          <CustomNumberTextField
            label="Valor deprecacion anual"
            name= 'valor_deprecacion_anual'
            //register={register("valor_deprecacion_anual")}
            control= {form.control}
            error={errors.valor_deprecacion_anual}
            helperText={errors.valor_deprecacion_anual?.message}
            shrink={shrink}
            min={0}
            max={1000}
            defaultValue={form.getValues().valor_deprecacion_anual || ''}
            size={gridSizeMdLg3}
            //readOnly={true}
            
            
          />

          <CustomNumberTextField
            label="Valor residual"
            name="valor_residual"
            //register={register("valor_residual")}
            error={errors.valor_residual}
            helperText={errors.valor_residual?.message}
            shrink={shrink}
            min={0}
            max={10000}
            control= {form.control}
            defaultValue={form.getValues().valor_residual || ''}
            size={gridSizeMdLg3}
            //readOnly={true}
          />
          <CustomNumberTextField
            label="años depreciacion"
            name='anios_deprecacion'
            ////register={register("anios_deprecacion")}
            error={errors.anios_deprecacion}
            helperText={errors.anios_deprecacion?.message}
            shrink={shrink}
            min={0}
            max={1000}
            size={gridSizeMdLg3}
            control={form.control}
            defaultValue={form.getValues().anios_deprecacion || ''}
            ///readOnly={true}
          />
          <CustomNumberTextField
            label="Porcentaje depreciacion"
            name= "porcentaje_depreciacion"
            ///register={register("porcentaje_depreciacion")}
            error={errors.porcentaje_depreciacion}
            helperText={errors.porcentaje_depreciacion?.message}
            shrink={shrink}
            min={0}
            max={10000}
            control={form.control}
            defaultValue={form.getValues().porcentaje_depreciacion || ''}
            //readOnly={true}
            size={gridSizeMdLg3}
          />

          <Grid item {...gridSizeMdLg12}>
            <Paper className="paper-producto">
              <Grid
                container
                spacing={1}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Grid item>
                  <TabTextLabel
                    text={"Depreciaciones"}
                    mt={0}
                    pb={0}
                    pl={0}
                    pt={0}
                  />
                </Grid>
                <div className="linea"></div>
                <Grid item xs={12}>
                  <SampleTable<Deprecacion>
                    columns={columnsDepreciaciones}
                    data={DeprecacionesPaging?.data.data || []}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </CustomTabPanel>
      </Grid>
    </SingleTableBoxScene>
  );
};
export default SaveActivos;
